import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileRef.current && !mobileRef.current.contains(event.target as Node)) {
        setMobileMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/80 backdrop-blur-md" ref={mobileRef}>
      <div className="px-4 md:px-10">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className="text-lg font-heading font-bold text-foreground-950 tracking-wide">YOYBUY<span className="text-primary-500">.SBS</span></span>
          </Link>

          <div className="hidden md:flex items-center gap-5">
            <Link to="/" className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">Главная</Link>

            <Link to="/guide/yoybuy-sheet-guide" className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">Руководство по покупкам</Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 bg-background-100 border border-background-300 rounded-lg text-sm text-foreground-800 hover:border-foreground-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span>Категории</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-background-50 border border-background-200 rounded-xl overflow-hidden max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-right-up-line text-foreground-400 text-xs w-3 h-3 flex items-center justify-center"></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-user-3-line text-base w-4 h-4 flex items-center justify-center"></i>
              <span>yoybuy sheet</span>
              <i className="ri-arrow-right-up-line text-base w-4 h-4 flex items-center justify-center"></i>
            </a>
          </div>

          <div className="md:hidden flex items-center gap-2">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-3 py-2 bg-primary-500 text-white text-xs font-semibold rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              yoybuy sheet
            </a>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-foreground-800 cursor-pointer"
              aria-label="Меню"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl w-5 h-5 flex items-center justify-center`}></i>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden pb-5 border-t border-background-200 mt-2 pt-4 max-h-[70vh] overflow-y-auto">
            <div className="space-y-1 mb-4">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 py-2.5 px-2 text-sm font-medium text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer">
                <i className="ri-home-4-line text-base w-4 h-4 flex items-center justify-center text-foreground-500"></i>
                <span className="whitespace-nowrap">Главная</span>
              </Link>
              <Link to="/guide/yoybuy-sheet-guide" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 py-2.5 px-2 text-sm font-medium text-foreground-900 hover:bg-background-100 rounded-lg transition-colors cursor-pointer">
                <i className="ri-book-open-line text-base w-4 h-4 flex items-center justify-center text-foreground-500"></i>
                <span className="whitespace-nowrap">Руководство по покупкам</span>
              </Link>
            </div>

            <p className="py-2 px-2 text-xs text-foreground-500 font-semibold uppercase tracking-wider whitespace-nowrap">
              Категории товаров
            </p>
            <div className="grid grid-cols-2 gap-1 mt-1">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 py-2.5 px-2 text-xs text-foreground-700 hover:bg-background-100 hover:text-foreground-950 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-up-line text-foreground-400 text-xs w-3 h-3 flex items-center justify-center flex-shrink-0"></i>
                  <span className="whitespace-nowrap truncate">{cat.name}</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}